<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_BUSINESS_AUTOMATION')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="tillAccountRef"
      :items="tillAccountList"
      :busy="isBusy"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_BUSINESS_AUTOMATION')"
              @click="onSelectTillAccount(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectTillAccount(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onDeleteTillAccount(data.item, $event.target)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50"> {{ $t('Delete') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <!-- Pagination -->
    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      no-close-on-backdrop
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="tillAccountRules">
        <b-form @submit.prevent="handleSubmit">
          <h6 class="section-label mb-2 mt-2">
            {{ $t('General Information') }}
          </h6>
          <b-row class="match-height">
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="tillAccountFormData.branchCode"
                :label="$t('Branch')"
                :options="branchOptions"
                :reduce="option => option.value"
                :error-name="$t('Branch')"
                rules="required"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
                :set-selected="setTellerUsers"
              />
            </b-col>
            <!-- Branch -->

            <!-- Account -->
            <b-col md="6">
              <SelectField
                id="account"
                v-model="tillAccountFormData.accountNumber"
                :label="$t('Account')"
                :options="accountTypeOptions"
                :reduce="option => option.value"
                :error-name="$t('Account')"
                rules="required"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
                :loading="isLoadingAccount"
              />
            </b-col>
            <!-- Account -->
          </b-row>

          <b-row class="match-height">
            <!-- Teller Name -->
            <b-col md="6">
              <SelectField
                id="teller-name"
                v-model="tillAccountFormData.tellerUserID"
                :label="$t('Teller Name')"
                :options="userOptions"
                :reduce="option => option.value"
                :error-name="$t('Teller Name')"
                rules="required"
                :placeholder="$t('Select')"
                item="label"
                :readonly="isReadonly"
                :loading="isLoadingTellerNames"
              />
            </b-col>
            <!-- Teller Name -->
            <!--Currency  -->
            <b-col md="6">
              <SelectField
                id="currency-code"
                v-model="tillAccountFormData.currencyCode"
                :label="$t('Currency')"
                :reduce="option => option"
                :options="currencyCodeOptions"
                item="label"
                :placeholder="$t('Select currency')"
                :readonly="isEditable"
              />
            </b-col>
            <!--Currency  -->
          </b-row>

          <h6 class="section-label mt-2">
            {{ $t('Limit Configurations') }}
          </h6>
          <b-row class="match-height align-items-center">

            <!--   Cash Volume Limit  -->
            <b-col
              md="6"
            >

              <SwitchButton
                v-model="tillAccountFormData.requireCashVolumeLimit"
                :label="$t('Cash Volume Limit?')"
                name="check-button"
                class="mt-2"
                :readonly="isReadonly"
              />
            </b-col>
            <!--   Cash Volume Limit  -->
          </b-row>

          <b-row
            v-if="setCashVolumeLimit(tillAccountFormData.requireCashVolumeLimit)"
            class="match-height"
          >

            <b-col md="12">
              <ValidationObserver>
                <b-row>
                  <!--  Max. Balance -->
                  <b-col md="6">
                    <InputNumberField
                      id="max-balance"
                      v-model.number="tillAccountFormData.maxBalance"
                      :label="$t('Max. Balance')"
                      :placeholder="$t('Enter max. balance')"
                      vid="maxFee"
                      :error-name="$t('Max. Balance')"
                      rules="required|integer|positive"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <!--  Max. Balance -->

                  <b-col md="6">
                    <InputNumberField
                      id="min-threshold-for-alert"
                      v-model.number="tillAccountFormData.minThresholdForAlert"
                      :label="$t('Min. Threshold For Alert')"
                      :placeholder="$t('Enter min. threshold for alert')"
                      :error-name="$t('Min. Threshold For Alert')"
                      rules="required|integer|amount:@maxFee|positive"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <!--  Min. Threshold For Alert -->

                </b-row>
              </ValidationObserver>
            </b-col>
            <!--  Min. Threshold For Alert -->

            <!--  Supervisor Email -->
            <b-col
              md="6"
            >
              <InputField
                id="supervisor-email"
                v-model="tillAccountFormData.supervisorEmail"
                :label="$t('Supervisor Email')"
                :placeholder="$t('Enter supervisor email')"
                type="text"
                rules="required|email"
                error-name="Supervisor Email"
                :readonly="isReadonly"
              />
            </b-col>
            <!--  Supervisor Email -->

            <!--  Cash Volume Limit -->
            <!-- <b-col md="6">
              <InputNumberField
                id="cash-volume-limit"
                v-model="tillAccountFormData.requireCashVolumeLimit"
                label="Cash Volume Limit"
                placeholder="Enter cash volume limit"
                error-name="Cash Volume Limit"
                rules="required|integer|positive"
                :readonly="isReadonly"
              />
            </b-col> -->
            <!--  Cash Volume Limit -->
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchTillAccount"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchCode"
            :label="$t('Branch')"
            :options="branchOptions"
            :reduce="option => option.value"
            item="label"
            :placeholder="$t('Select')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Branch -->

        <!-- Account -->
        <b-col md="6">
          <SelectField
            id="account"
            v-model="filter.accountNumber"
            :label="$t('Account')"
            :options="accountTypeOptions"
            :reduce="option => option.value"
            item="label"
            :placeholder="$t('Select')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Account -->

        <!-- Teller Name -->
        <b-col md="6">
          <SelectField
            id="teller-name"
            v-model="filter.tellerUserID"
            :label="$t('Teller Name')"
            :placeholder="$t('Select')"
            :options="tellerUsersOptions"
            :reduce="option => option.value"
            item="label"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Teller Name -->

        <!--  Supervisor Email -->
        <b-col
          md="6"
        >
          <InputField
            id="supervisor-email"
            v-model="filter.supervisorEmail"
            :label="$t('Supervisor Email')"
            :placeholder="$t('Enter supervisor email')"
            type="text"
            for="supervisor-email"
            rules="email"
            :error-name="$t('Supervisor Email')"
            :readonly="isReadonly"
          />
        </b-col>
        <!--  Supervisor Email -->
        <b-col md="6">
          <SelectField
            id="currency-code"
            v-model="filter.currencyCode"
            :label="$t('Currency')"
            :reduce="option => option"
            :options="currencyCodeOptions"
            item="label"
            :placeholder="$t('Select currency')"
          />
        </b-col>
      </b-row>
      <b-row>
        <!--  Max. Balance -->
        <b-col md="6">
          <InputNumberField
            id="max-balance"
            v-model.number="filter.maxBalance"
            :label="$t('Max. Balance')"
            :placeholder="$t('Enter max. balance')"
            vid="maxFee"
            :error-name="$t('Max. Balance')"
            rules="integer|positive"
            :readonly="isReadonly"
          />
        </b-col>
        <!--  Max. Balance -->

        <b-col md="6">
          <InputNumberField
            id="min-threshold-for-alert"
            v-model="filter.minThresholdForAlert"
            :label="$t('Min. Threshold For Alert')"
            :placeholder="$t('Enter min. threshold for alert')"
            :error-name="$t('Min. Threshold For Alert')"
            rules="integer|positive"
            :readonly="isReadonly"
          />
        </b-col>
        <!--  Min. Threshold For Alert -->

        <!--  Cash Volume Limit -->
        <!-- <b-col md="6">
          <InputNumberField
            id="cash-volume-limit"
            v-model="tillAccountFormData.requireCashVolumeLimit"
            label="Cash Volume Limit"
            placeholder="Enter cash volume limit"
            error-name="Cash Volume Limit"
            rules="integer|positive"
            :readonly="isReadonly"
          />
        </b-col> -->
        <!--  Cash Volume Limit -->

      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BModal,
  BForm, BSpinner, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { toastAlert } from '@core/mixins/toast'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import { toBalance, toKobo } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import InputNumberField from '../../../forms/InputNumberField.vue'
import Button from '../../../components/button/Button.vue'
import SwitchButton from '../../../components/switch/Switch.vue'
import { operationPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    SwitchButton,
    TableEntries,
    InputNumberField,
    Button,
    BTable,
    TablePagination,
    BModal,
    BForm,
    BSpinner,
    ValidationObserver,
    InputField,
    SelectField,
    BDropdown,
    BDropdownItem,
    FullScreenSpinner,
  },
  // mixins: [modalMixins, tableMixins, sweetAlert],
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin, toastAlert],
  data() {
    return {
      sortBy: 'tellerName',
      isReadonly: false,
      isEditable: false,
      required,
      isRequired: false,
      isLoadingTellerNames: false,
      isLoading: false,
      isLoadingAccount: false,
      sortDesc: false,
      showSpinner: false,
      isBusy: true,
      buttonLabel: this.$t('Save'),
      modalTitle: this.$t('Create New Till Account'),
      accountUsers: [],
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'tellerName', sortable: true, label: 'Teller Name' },
        { key: 'branchName', sortable: true, label: 'Branch' },
        { key: 'currencyCode', sortable: true, label: 'Currency' },
        { key: 'maxBalance', sortable: true, label: 'Max. Balance' },
        { key: 'minThresholdForAlert', sortable: true, label: 'Min. Threshold For Alert' },
        { key: 'actions', label: 'Actions' },
      ],
      userOptions: [],
      tellerUsersOptions: [],
      tillAccountFormData: {
        id: '',
        branchCode: '',
        accountNumber: '',
        currencyCode: '',
        minThresholdForAlert: 0,
        tellerUserID: '',
        maxBalance: 0,
        requireCashVolumeLimit: false,
        supervisorEmail: '',
      },
      requireCashVolumeLimit: false,
      cashVolumeOptions: [
        { label: 'Unrequired', value: 1 },
        { label: 'Required', value: 2 },
      ],
      timeout: null,
      tillAccountList: [],
      nameOption: [],
      accountTypeOptions: [],
      filter: {
        branchCode: '',
        accountNumber: '',
        tellerUserID: '',
        currencyCode: '',
        tellerName: '',
        requireCashVolumeLimit: '',
        maxBalance: '',
        supervisorEmail: '',
        minThresholdForAlert: '',
        sortColumn: '',
      },
      currencyCodeOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchTillAccount()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchTillAccount()
    },
  },
  beforeDestroy() {
    clearTimeout(this.timeout)
  },
  mounted() {
    this.fetchTillAccount()
    this.getAccounts()
    this.fetchUsers()
    this.fetchAllCurrencies()
  },
  methods: {
    ...mapActions({
      getTillAccountList: 'operations/getTillAccountList',
      createTillAccount: 'operations/createTillAccountAction',
      updateTillAccount: 'operations/updateTillAccountAction',
      getAccountCategoryByCode: 'account/getAccountCategoryByCode',
      getUsersByBranchCode: 'operations/getUsersByBranchCode',
      getUsers: 'admin/getUsers',
      deleteTillAccount: 'operations/deleteBusinessAutomation',
      getAllCurrencies: 'account/getAllCurrenciesAction',
    }),
    async fetchAllCurrencies() {
      try {
        const response = await this.getAllCurrencies({ permission: operationPermissions.VIEW_BUSINESS_AUTOMATION })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.currencyCodeOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, curr.code], [])
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    async getAccounts() {
      try {
        this.isLoadingAccount = true
        const { data, status } = await this.getAccountCategoryByCode(10103000)

        if (status === 200 && data.data) {
          this.accountTypeOptions = data.data.filter(item => item.status === 'Active').map(item => ({
            label: item.name,
            value: item.code.toString(),
          // eslint-disable-next-line no-nested-ternary
          })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoadingAccount = false
      }
    },
    async tillAccountAction() {
      try {
        // this.isLoading = true
        // const button = document.querySelector('.btn-primary')
        // button.innerHTML += '<span role="status" class="spinner-border"><span class="sr-only">Spinning</span></span>'
        const formData = {
          id: this.tillAccountFormData.id,
          branchCode: this.tillAccountFormData.branchCode,
          accountNumber: this.tillAccountFormData.accountNumber,
          tellerUserID: this.tillAccountFormData.tellerUserID,
          requireCashVolumeLimit: this.tillAccountFormData.requireCashVolumeLimit,
          supervisorEmail: this.tillAccountFormData.supervisorEmail,
          maxBalance: toKobo(this.tillAccountFormData.maxBalance),
          minThresholdForAlert: toKobo(this.tillAccountFormData.minThresholdForAlert),
          currencyCode: this.tillAccountFormData.currencyCode,
        }
        const { status } = this.buttonLabel === 'Save'
          ? await this.createTillAccount(formData)
          : await this.updateTillAccount(formData)

        if (status === 200) {
          this.fetchTillAccount()
          this.hideModal('modal-lg')
          const yourRequest = this.$t('Your request to')
          const successText = this.$t('Till account was successfully')
          this.alertSuccess({ text: `${yourRequest} ${this.buttonLabel} ${successText}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    async setTellerUsers(selectedValue) {
      try {
        this.isLoadingTellerNames = true
        this.userOptions = []
        const { data, status } = await this.getUsersByBranchCode(selectedValue)
        if (status === 200 && data.data) {
          this.userOptions = data.data.filter(item => item.status === 'Active').map(item => ({
            label: `${item?.lastname ?? ''} ${item?.firstname ?? ''} ${item?.othernames ?? ''}`,
            value: item.id,
          // eslint-disable-next-line no-nested-ternary
          })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.toastAlert({ title: error })
      } finally {
        this.isLoadingTellerNames = false
      }
    },
    async fetchUsers() {
      try {
        this.isBusy = true
        const { status, data } = await this.getUsers({
          search: { status: 'Active' },
          page: 0,
          limit: 0,
          permission: operationPermissions.VIEW_BUSINESS_AUTOMATION,
        })

        if (status === 200 && data.data) {
          this.tellerUsersOptions = data.data.map(item => ({
            label: `${item.lastname ? item.lastname : ''} ${item.firstname ? item.firstname : ''} ${item.othernames ? item.othernames : ''}`,
            value: item.id,
          // eslint-disable-next-line no-nested-ternary
          })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async onSelectTillAccount(items, activity, val) {
      this.isEditable = true
      this.isReadonly = val !== 'edit'
      this.modalTitle = val === 'edit' ? this.$t('Edit Till Account') : this.$t('View Till Account')

      try {
        this.showSpinner = true
        this.timeout = setTimeout(async () => {
          await this.setTellerUsers(items.branchCode)
        }, 1000)

        this.tillAccountFormData = {
          ...items,
          maxBalance: items.maxBalance !== 0 ? items.maxBalance : '0',
          minThresholdForAlert: items.minThresholdForAlert !== 0 ? items.minThresholdForAlert : '0',
        }
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async fetchTillAccount() {
      try {
        this.isBusy = true
        const { status, data } = await this.getTillAccountList({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: operationPermissions.VIEW_BUSINESS_AUTOMATION,
        })

        if (status === 200 && data.data) {
          this.tillAccountList = data.data.map(item => ({
            ...item,
            maxBalance: toBalance(item.maxBalance),
            minThresholdForAlert: toBalance(item.minThresholdForAlert),
          }))
          this.totalCount = data.recordCount
        } else {
          this.tillAccountList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async onDeleteTillAccount(item) {
      const deleteTillAccount = async () => {
        try {
          this.showSpinner = true
          const { status } = await this.deleteTillAccount({ routeName: 'TillAccount', id: item.id })
          if (status === 200) {
            this.fetchTillAccount()
            this.alertSuccess({ text: this.$t('Deleted successfully') })
          }
        } catch (error) {
          const deleteText = this.$t('Your request to Delete Till Account failed. Reason:')
          error.response.data.errors.forEach(responseError => {
            const errorMessage = `${deleteText} ${responseError.errorMessage}`
            this.alertError({ text: errorMessage })
          })
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(deleteTillAccount, { text: this.$t('Are you sure you want to delete this Till Account?') })
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.tillAccountRules.validate().then(success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const thisTill = this.$t('this Till Account?')
          this.confirm(this.tillAccountAction, { text: `${areYouSure} ${this.buttonLabel} ${thisTill}` })
        }
      })
    },
    setCashVolumeLimit(value) {
      if (!value) {
        this.tillAccountFormData.maxBalance = 0
        this.tillAccountFormData.minThresholdForAlert = 0
        this.tillAccountFormData.supervisorEmail = ''
        return false
      }
      return true
    },
    resetModal() {
      this.tillAccountFormData = { ...null }
      this.isLoading = false
      this.isReadonly = false
      this.modalTitle = this.$t('Create New Till Account')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchTillAccount()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
